/**
 * Lekonas Collection Tabs
 * File: assets/lekonas-collection-tabs.js
 */

(() => {
  const SECTION_SELECTOR = '[data-collection-tabs-section]';
  const MOBILE_QUERY = '(max-width: 749px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  class LekonasCollectionTabs {
    constructor(section) {
      this.section = section;
      this.tabs = Array.from(
        section.querySelectorAll('[data-collection-tab]')
      );
      this.panels = Array.from(
        section.querySelectorAll('[data-collection-panel]')
      );

      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionMedia = window.matchMedia(
        REDUCED_MOTION_QUERY
      );

      this.panelSliders = new Map();
      this.resizeTimer = null;

      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
      this.handleResize = this.handleResize.bind(this);

      this.init();
    }

    init() {
      if (!this.tabs.length || !this.panels.length) {
        return;
      }

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick);
        tab.addEventListener('keydown', this.handleTabKeydown);
      });

      this.panels.forEach((panel) => {
        this.initializePanelSlider(panel);
      });

      window.addEventListener('resize', this.handleResize);

      if (typeof this.mobileMedia.addEventListener === 'function') {
        this.mobileMedia.addEventListener(
          'change',
          this.handleResize
        );
      } else if (typeof this.mobileMedia.addListener === 'function') {
        this.mobileMedia.addListener(this.handleResize);
      }

      const initiallySelectedTab =
        this.tabs.find(
          (tab) => tab.getAttribute('aria-selected') === 'true'
        ) || this.tabs[0];

      if (initiallySelectedTab) {
        this.activateTab(initiallySelectedTab, {
          moveFocus: false,
          scrollTabIntoView: false,
          resetPanelSlider: false
        });
      }
    }

    destroy() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.handleTabClick);
        tab.removeEventListener(
          'keydown',
          this.handleTabKeydown
        );
      });

      this.panelSliders.forEach((slider) => {
        slider.destroy();
      });

      this.panelSliders.clear();

      window.removeEventListener('resize', this.handleResize);

      if (typeof this.mobileMedia.removeEventListener === 'function') {
        this.mobileMedia.removeEventListener(
          'change',
          this.handleResize
        );
      } else if (
        typeof this.mobileMedia.removeListener === 'function'
      ) {
        this.mobileMedia.removeListener(this.handleResize);
      }
    }

    handleTabClick(event) {
      const tab = event.currentTarget;

      this.activateTab(tab, {
        moveFocus: false,
        scrollTabIntoView: true,
        resetPanelSlider: true
      });
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);

      if (currentIndex < 0) {
        return;
      }

      let nextIndex = null;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % this.tabs.length;
          break;

        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex =
            (currentIndex - 1 + this.tabs.length) %
            this.tabs.length;
          break;

        case 'Home':
          nextIndex = 0;
          break;

        case 'End':
          nextIndex = this.tabs.length - 1;
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();

          this.activateTab(event.currentTarget, {
            moveFocus: true,
            scrollTabIntoView: true,
            resetPanelSlider: true
          });

          return;

        default:
          return;
      }

      event.preventDefault();

      const nextTab = this.tabs[nextIndex];

      this.activateTab(nextTab, {
        moveFocus: true,
        scrollTabIntoView: true,
        resetPanelSlider: true
      });
    }

    activateTab(
      selectedTab,
      {
        moveFocus = false,
        scrollTabIntoView = true,
        resetPanelSlider = true
      } = {}
    ) {
      if (!selectedTab) {
        return;
      }

      const selectedTabId = selectedTab.dataset.tabId;
      const selectedPanel = this.panels.find(
        (panel) => panel.dataset.panelId === selectedTabId
      );

      if (!selectedPanel) {
        return;
      }

      this.tabs.forEach((tab) => {
        const isSelected = tab === selectedTab;

        tab.classList.toggle('is-active', isSelected);
        tab.setAttribute(
          'aria-selected',
          isSelected ? 'true' : 'false'
        );
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');
      });

      this.panels.forEach((panel) => {
        const isSelected = panel === selectedPanel;

        panel.classList.toggle('is-active', isSelected);
        panel.hidden = !isSelected;
      });

      if (moveFocus) {
        selectedTab.focus({ preventScroll: true });
      }

      if (scrollTabIntoView) {
        selectedTab.scrollIntoView({
          behavior: this.reducedMotionMedia.matches
            ? 'auto'
            : 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }

      const slider = this.panelSliders.get(selectedPanel);

      if (slider) {
        window.requestAnimationFrame(() => {
          slider.refresh();

          if (resetPanelSlider) {
            slider.goTo(0, false);
          }
        });
      }
    }

    initializePanelSlider(panel) {
      if (this.panelSliders.has(panel)) {
        return;
      }

      const track = panel.querySelector(
        '[data-collection-products-track]'
      );
      const slides = Array.from(
        panel.querySelectorAll(
          '[data-collection-product-slide]'
        )
      );
      const previousButton = panel.querySelector(
        '[data-panel-previous]'
      );
      const nextButton = panel.querySelector('[data-panel-next]');
      const currentCounter = panel.querySelector(
        '[data-panel-current]'
      );

      if (
        !track ||
        slides.length < 2 ||
        !previousButton ||
        !nextButton ||
        !currentCounter
      ) {
        return;
      }

      const slider = new LekonasPanelSlider({
        section: this.section,
        panel,
        track,
        slides,
        previousButton,
        nextButton,
        currentCounter,
        mobileMedia: this.mobileMedia,
        reducedMotionMedia: this.reducedMotionMedia
      });

      this.panelSliders.set(panel, slider);
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        this.panelSliders.forEach((slider) => {
          slider.refresh();
        });
      }, 140);
    }
  }

  class LekonasPanelSlider {
    constructor({
      section,
      panel,
      track,
      slides,
      previousButton,
      nextButton,
      currentCounter,
      mobileMedia,
      reducedMotionMedia
    }) {
      this.section = section;
      this.panel = panel;
      this.track = track;
      this.slides = slides;
      this.previousButton = previousButton;
      this.nextButton = nextButton;
      this.currentCounter = currentCounter;
      this.mobileMedia = mobileMedia;
      this.reducedMotionMedia = reducedMotionMedia;

      this.activeIndex = 0;
      this.scrollTimer = null;
      this.isProgrammaticScroll = false;

      this.handlePreviousClick =
        this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);

      this.init();
    }

    init() {
      this.previousButton.addEventListener(
        'click',
        this.handlePreviousClick
      );
      this.nextButton.addEventListener(
        'click',
        this.handleNextClick
      );
      this.track.addEventListener('scroll', this.handleScroll, {
        passive: true
      });

      this.refresh();
    }

    destroy() {
      this.previousButton.removeEventListener(
        'click',
        this.handlePreviousClick
      );
      this.nextButton.removeEventListener(
        'click',
        this.handleNextClick
      );
      this.track.removeEventListener(
        'scroll',
        this.handleScroll
      );

      window.clearTimeout(this.scrollTimer);
    }

    isActive() {
      return (
        this.mobileMedia.matches &&
        this.section.classList.contains(
          'lekonas-collection-tabs--mobile-slider'
        )
      );
    }

    getMaximumScroll() {
      return Math.max(
        0,
        this.track.scrollWidth - this.track.clientWidth
      );
    }

    getSlidePosition(index) {
      const slide = this.slides[index];

      if (!slide) {
        return 0;
      }

      const position =
        slide.offsetLeft - this.track.offsetLeft;

      return Math.max(
        0,
        Math.min(position, this.getMaximumScroll())
      );
    }

    getClosestSlideIndex() {
      const currentScroll = this.track.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const slidePosition = this.getSlidePosition(index);
        const distance = Math.abs(
          slidePosition - currentScroll
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    goTo(index, smooth = true) {
      this.activeIndex = Math.max(
        0,
        Math.min(index, this.slides.length - 1)
      );

      if (!this.isActive()) {
        this.updateControls();
        return;
      }

      this.isProgrammaticScroll = true;

      this.track.scrollTo({
        left: this.getSlidePosition(this.activeIndex),
        behavior:
          smooth && !this.reducedMotionMedia.matches
            ? 'smooth'
            : 'auto'
      });

      this.updateControls();

      window.clearTimeout(this.scrollTimer);

      this.scrollTimer = window.setTimeout(() => {
        this.isProgrammaticScroll = false;
        this.activeIndex = this.getClosestSlideIndex();
        this.updateControls();
      }, smooth ? 350 : 0);
    }

    refresh() {
      if (!this.isActive()) {
        this.activeIndex = 0;
        this.currentCounter.textContent = '1';
        this.previousButton.disabled = true;
        this.nextButton.disabled = false;
        this.setAriaDisabled();
        return;
      }

      this.activeIndex = this.getClosestSlideIndex();
      this.updateControls();
    }

    handlePreviousClick() {
      this.goTo(this.activeIndex - 1);
    }

    handleNextClick() {
      this.goTo(this.activeIndex + 1);
    }

    handleScroll() {
      if (!this.isActive()) {
        return;
      }

      window.clearTimeout(this.scrollTimer);

      this.scrollTimer = window.setTimeout(() => {
        if (this.isProgrammaticScroll) {
          return;
        }

        this.activeIndex = this.getClosestSlideIndex();
        this.updateControls();
      }, 90);
    }

    updateControls() {
      this.currentCounter.textContent = String(
        this.activeIndex + 1
      );

      const maximumScroll = this.getMaximumScroll();
      const atStart =
        this.activeIndex <= 0 || this.track.scrollLeft <= 2;
      const atEnd =
        this.activeIndex >= this.slides.length - 1 ||
        this.track.scrollLeft >= maximumScroll - 2;

      this.previousButton.disabled = atStart;
      this.nextButton.disabled = atEnd;

      this.setAriaDisabled();
    }

    setAriaDisabled() {
      this.previousButton.setAttribute(
        'aria-disabled',
        this.previousButton.disabled ? 'true' : 'false'
      );
      this.nextButton.setAttribute(
        'aria-disabled',
        this.nextButton.disabled ? 'true' : 'false'
      );
    }
  }

  function initializeSection(section) {
    if (
      !section ||
      section.dataset.collectionTabsInitialized === 'true'
    ) {
      return;
    }

    section.dataset.collectionTabsInitialized = 'true';
    section.collectionTabsInstance =
      new LekonasCollectionTabs(section);
  }

  function destroySection(section) {
    if (!section) {
      return;
    }

    if (section.collectionTabsInstance) {
      section.collectionTabsInstance.destroy();
      delete section.collectionTabsInstance;
    }

    section.dataset.collectionTabsInitialized = 'false';
  }

  function initializeAll(scope = document) {
    if (scope.matches?.(SECTION_SELECTOR)) {
      initializeSection(scope);
    }

    scope.querySelectorAll?.(SECTION_SELECTOR).forEach(
      initializeSection
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeAll();
    });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section = event.target.matches?.(SECTION_SELECTOR)
        ? event.target
        : event.target.querySelector?.(SECTION_SELECTOR);

      destroySection(section);
    }
  );

  document.addEventListener(
    'shopify:section:reorder',
    () => {
      initializeAll();
    }
  );

  document.addEventListener('shopify:block:select', (event) => {
    const blockElement = event.target;
    const section = blockElement.closest?.(SECTION_SELECTOR);

    if (!section || !section.collectionTabsInstance) {
      return;
    }

    const selectedTab = section.querySelector(
      `[data-collection-tab][data-tab-id="${event.detail.blockId}"]`
    );

    if (selectedTab) {
      section.collectionTabsInstance.activateTab(selectedTab, {
        moveFocus: false,
        scrollTabIntoView: true,
        resetPanelSlider: false
      });
    }
  });
})();
